import React, { useState, useRef } from 'react';
import { TrendingUp, AlertCircle, Filter, Sparkles, Info, ChevronDown } from 'lucide-react';
import SNPCard from './SNPCard';

const PAGE_SIZE = 25;

const ResultsSection = ({ results, onNewUpload, onSnpClick, showingFiltered, totalUnfilteredSnps, isPartial }) => {
  const [loadedCount, setLoadedCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef(null);

  const visibleSnps = results.snps.slice(0, loadedCount);
  const hasMore = loadedCount < results.snps.length;

  const handleLoadMore = () => setLoadedCount(prev => prev + PAGE_SIZE);

  const gwasCount = results.snps.filter(s => s.gwas).length;
  const gwasRate = results.snps.length > 0
    ? Math.round((gwasCount / results.snps.length) * 100)
    : 0;

  return (
    <div className="space-y-5">
      {/* Header card */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Analysis Results
                {isPartial && (
                  <span className="ml-3 text-sm font-normal text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    Loading GWAS…
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {results.snps.length.toLocaleString()} clinically relevant variants found
              </p>
            </div>
          </div>
          <button
            onClick={onNewUpload}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700"
          >
            Upload New File
          </button>
        </div>

        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              <strong>Risk variants don't guarantee disease.</strong> Genetic risk is multifactorial —
              a "2x risk" variant means twice the population average, but if baseline risk is 1%, yours is still only 2%.
              Consult a genetic counselor for personalized interpretation.
            </p>
          </div>
        </div>

        {gwasCount > 0 && (
          <div className="mb-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <p className="text-xs text-purple-800">
              <strong>{gwasCount.toLocaleString()}</strong> of {results.snps.length.toLocaleString()} variants ({gwasRate}%) enriched with GWAS Catalog research data
            </p>
          </div>
        )}

        {showingFiltered && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
            <Filter className="w-4 h-4" />
            <span>Showing {results.snps.length} of {totalUnfilteredSnps} SNPs (filtered)</span>
          </div>
        )}
      </div>

      {/* SNP cards */}
      <div className="space-y-2">
        {visibleSnps.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {showingFiltered
                ? 'No SNPs match the selected filters.'
                : 'No matching SNPs found in the ClinVar database.'}
            </p>
          </div>
        )}

        {visibleSnps.map((snp, idx) => (
          <SNPCard key={`${snp.rsid}-${idx}`} snp={snp} onClick={() => onSnpClick(snp)} />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div ref={loadMoreRef} className="text-center">
          <button
            onClick={handleLoadMore}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 transition-colors shadow-sm"
          >
            <ChevronDown className="w-4 h-4" />
            Load more
            <span className="text-xs text-gray-500">
              ({Math.min(PAGE_SIZE, results.snps.length - loadedCount).toLocaleString()} more)
            </span>
          </button>
        </div>
      )}

      {!hasMore && results.snps.length > 0 && (
        <p className="text-center text-xs text-gray-400">
          Showing all {results.snps.length.toLocaleString()} variants
        </p>
      )}
    </div>
  );
};

export default ResultsSection;